import pdfMake from 'pdfmake/build/pdfmake';
import JsBarcode from 'jsbarcode';
import { IStickerPdf } from '../../../../services/model/IStickerPdf';
import { logo2025Img } from '../../../../services/others/assets';

/**
 * Product Sticker PDF Generator
 * 用于 logoType 为 Normal (0), With Customer Logo (1), 和 Without Jadcup Logo (2) 的产品标签
 */
export default async (data: IStickerPdf[], required: string) => {
  const vfsFonts = await import("pdfmake/build/vfs_fonts");
  const { vfs } = vfsFonts.pdfMake;
  pdfMake.vfs = vfs;

  const documentDefinition: any = {
    pageSize: {
      width: 290,
      height: 435
    },
    content: generateContent(data),
    pageMargins: [15, 10, 15, 10],
  };

  if (required === 'print') {
    pdfMake.createPdf(documentDefinition).print({});
  }
  if (required === 'view') {
    pdfMake.createPdf(documentDefinition).open();
  }
};

// 将文本转换为 base64 条形码
const textToBase64Barcode = (text: string) => {
  const canvas = document.createElement('canvas');
  JsBarcode(canvas, text, {
    format: "CODE128",
    width: 2,
    height: 80,
    displayValue: false,
    margin: 0
  });
  return canvas.toDataURL('image/png');
};

// 获取当前日期
const getCurrentDate = () => {
  const today = new Date();
  const dd = String(today.getDate()).padStart(2, '0');
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const yyyy = today.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

// 格式化日期
const formatDate = (dateInput: any): string => {
  let date: Date;

  // 如果是 moment 对象或有 format 方法的对象
  if (dateInput && typeof dateInput.format === 'function') {
    return dateInput.format('DD/MM/YYYY');
  }

  // 如果是字符串
  if (typeof dateInput === 'string') {
    date = new Date(dateInput + '.000Z');
  } else if (dateInput instanceof Date) {
    date = dateInput;
  } else {
    return getCurrentDate();
  }

  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

// 获取纸箱尺寸字符串
const getCartonSize = (item: IStickerPdf): string => {
  if (item.cartonLength != null && item.cartonWidth != null && item.cartonHeight != null) {
    return `${item.cartonLength}×${item.cartonWidth}×${item.cartonHeight}`;
  }
  return '';
};

// 生成 PDF 内容
const generateContent = (data: IStickerPdf[]) => {
  const content: any[] = [];

  data.forEach((item) => {
    // 1. Logo - 根据logoType选择显示客户logo或Jadcup logo
    // logoType: 0=Normal(Jadcup), 1=Customer Logo, 2=Without Jadcup Logo
    if (item.logoType !== 2) {  // logoType=2时不显示logo
      const logoImage = (item.logoType === 1 && item.logoBase64) ? item.logoBase64 : logo2025Img;
      content.push({
        image: logoImage,
        fit: [260, 80],        // ✅ pdfMake 会按原始比例缩放，不超过 75×75

        alignment: 'center',
        margin: [0, 5, 0, 0]
      });
    }

    // 2. 蓝色背景条 + 大写产品代码
    content.push({
      table: {
        widths: ['*'],
        body: [
          [{
            text: item.custProductCode?.toUpperCase() || '',
            fontSize: 18,
            bold: true,
            alignment: 'center',
            color: '#000000',
            // background: '#ADD8E6', // Light blue background
            border: [false, false, false, true], // 只显示底部边框
            margin: [0, 5, 0, 10]
          }]
        ]
      },
      // layout: 'noBorders',
      margin: [0, 5, 0, 0]  // 增加底部间距
    });

    // 3. 产品名称 (Title case) - 使用固定高度容器，最多三排
    const titleCaseName = toTitleCase(item.productName || '');
    content.push({
      table: {
        widths: ['*'],
        heights: [45],  // ✅ 固定整行高度，超出内容会被裁剪

        body: [
          [{
            stack: [
              { text: 'Product Name:', fontSize: 11, bold: true, alignment: 'left' },
              { text: titleCaseName, fontSize: 11,  alignment: 'left', noWrap: false }
            ]
          }]
        ]
      },
      layout: 'noBorders',
      margin: [5, 5, 0, 0]
    });



    // 5. 规格表格 (两列布局，每列内标签在上、数值在下)
    const cartonSize = getCartonSize(item);
    // 如果 sleevePkt == 1，显示 N/A，否则显示 packaging
    const packagingSpec = (item.sleeveQty === 1) ? 'N/A' : (item.packaging || '');
    const grossWeight = item.grossWeigh || '';
    const quantity = item.quantity?.toString() || '';

    content.push({
      table: {
        widths: ['*', '*'],
        body: [
          // 第一行: Quantity (左列) 和 Packaging Spec (右列)
          [
            {
              stack: [
                { text: 'Quantity:', fontSize: 11, bold: true, alignment: 'left', margin: [0, 2, 0, 2] },
                { text: quantity, fontSize: 11, alignment: 'left', margin: [0, 2, 0, 2] }
              ],
              border: [false, true, true, false]  // 左, 上, 右, 下
            },
            {
              stack: [
                { text: 'Packaging Spec:', fontSize: 11, bold: true, alignment: 'left', margin: [2, 2, 0, 2] },
                { text: packagingSpec, fontSize: 11, alignment: 'left', margin: [2, 2, 0, 2] }
              ],
              border: [false, true, false, false]
            }
          ],
          // 第二行: Carton Size (左列) 和 Gross Weight (右列)
          [
            {
              stack: [
                { text: 'Carton Size:', fontSize: 11, bold: true, alignment: 'left', margin: [0, 2, 0, 2] },
                { text: cartonSize+' mm' || ' ', fontSize: 11, alignment: 'left', margin: [0, 2, 0, 2] }
              ],
              border: [false, true, true, true]
            },
            {
              stack: [
                { text: 'Gross Weight:', fontSize: 11, bold: true, alignment: 'left', margin: [2, 2, 0, 2] },
                { text: grossWeight +' kg'|| ' ', fontSize: 11, alignment: 'left', margin: [2, 2, 0, 2] }
              ],
              border: [false, true, false, true]
            }
          ]
        ]
      },
      // layout: 'noBorders',
      margin: [0, 0, 0, 10]
    });

    // 6. 批号和制造日期
    const batchNumber = item.batchNumber || '';
    console.log("item.manufactureDate",item.manufactureDate);
        // console.log("item.manufactureDate",formatDate(item.manufactureDate));

    const manufactureDate = item.manufactureDate
      ? formatDate(item.manufactureDate)
      : getCurrentDate();

    if (batchNumber) {
      content.push({
        text: `Batch Number: ${batchNumber}`,
        fontSize: 11,
        alignment: 'center',
        margin: [0, 0, 0, 2]
      });
    }

    content.push({
      text: `Manufacture Date: ${manufactureDate}`,
      fontSize: 11,
      alignment: 'center',
      margin: [0, 0, 0, 5]
    });

    // 7. 条形码
    content.push({
      image: textToBase64Barcode(item.barcode),
      width: 200,
      height: 50,
      alignment: 'center',
      margin: [0, 0, 0, 5]
    });

    // 8. 条形码数字
    content.push({
      text: item.barcode,
      fontSize: 11,
      alignment: 'center',
      margin: [0, 0, 0, 5]
    });

    // 9. 底部 - Made in New Zealand
    if (item.madeInNz) {
      content.push({
        text: item.madeInNz,
        fontSize: 15,
        bold: true,
        alignment: 'center',
        margin: [0, 0, 0, 0]
      });
    }

    // 页面分隔（除了最后一项）
    if (data.indexOf(item) !== data.length - 1) {
      content.push({ text: '', pageBreak: 'after' });
    }
  });

  return content;
};

// 转换为 Title Case
const toTitleCase = (str: string): string => {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
